import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AutoGrowTextarea } from '@/components/ui/autoGrowTextarea';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { PageContainer } from '@/components/layout/PageContainer';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { PreVisitAnswersList } from '@/components/common/PreVisitAnswersList';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { usePatientQuery } from '@/hooks/usePatientsQueries';
import {
  useConsultationIntakeFieldsQuery,
  useConsultationOutcomeOptionsQuery,
} from '@/hooks/useConsultationConfig';
import { useWorkspacePreVisitQuestionsQuery } from '@/hooks/usePreVisitQuestions';
import {
  useCreateFirstConsultationMutation,
  usePatientFirstConsultationQuery,
  useRemoveFirstConsultationDocumentMutation,
  useUpdateFirstConsultationMutation,
  useUploadFirstConsultationDocumentMutation,
} from '@/hooks/useFirstConsultation';
import type {
  FirstConsultation,
  FirstConsultationDocument,
  FirstConsultationWritePayload,
  IntakeField,
} from '@/api/firstConsultations/firstConsultationsApi.types';
import { buildPreVisitQuestionsByCode, type PreVisitPhoto } from '@/api/preVisit/preVisitApi.types';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { MEDIA_IMAGE_CONTENT_TYPES, buildAcceptAttribute } from '@/constants/media';
import { CONSULTATION_INTAKE_SECTION_ORDER } from '@/constants/consultationIntakeSections';
import { FirstConsultationNotesCards } from './FirstConsultationNotesCards';
import { FirstConsultationSharingCard } from './FirstConsultationSharingCard';
import { localizedLabel } from './localizedLabel';

const FIRST_CONSULTATION_DOCUMENT_ACCEPT_ATTRIBUTE =
  'application/pdf,' + buildAcceptAttribute(MEDIA_IMAGE_CONTENT_TYPES);

function groupFieldsBySection(fields: IntakeField[]): Record<string, IntakeField[]> {
  const grouped: Record<string, IntakeField[]> = {};
  for (const field of fields) {
    const sectionFields = grouped[field.section];
    if (sectionFields === undefined) {
      grouped[field.section] = [field];
    } else {
      sectionFields.push(field);
    }
  }
  return grouped;
}

export function FirstConsultationEditor() {
  const { t } = useTranslation(['consultations', 'common']);
  const navigate = useNavigate();
  const { patientId, consultationId } = useParams<{
    patientId: string;
    consultationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const resolvedPatientId = patientId === undefined ? '' : patientId;
  const routeConsultationId = consultationId === undefined ? 'new' : consultationId;
  const isNew = routeConsultationId === 'new';
  const appointmentId = searchParams.get('appointmentId');

  const patientQuery = usePatientQuery(resolvedPatientId);
  const intakeFieldsQuery = useConsultationIntakeFieldsQuery();
  const outcomeOptionsQuery = useConsultationOutcomeOptionsQuery();
  const preVisitQuestionsQuery = useWorkspacePreVisitQuestionsQuery();
  const consultationQuery = usePatientFirstConsultationQuery(resolvedPatientId);

  const createMutation = useCreateFirstConsultationMutation();
  const updateMutation = useUpdateFirstConsultationMutation();
  const uploadDocumentMutation = useUploadFirstConsultationDocumentMutation();
  const removeDocumentMutation = useRemoveFirstConsultationDocumentMutation();
  const documentInputRef = useRef<HTMLInputElement>(null);

  const [isInitialized, setIsInitialized] = useState(false);
  const [resolvedConsultationId, setResolvedConsultationId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [therapistNotes, setTherapistNotes] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  const [outcomeCode, setOutcomeCode] = useState('');
  const [documents, setDocuments] = useState<FirstConsultationDocument[]>([]);
  const [previewedDocument, setPreviewedDocument] = useState<FirstConsultationDocument | null>(
    null
  );
  const [previewedPhoto, setPreviewedPhoto] = useState<PreVisitPhoto | null>(null);
  const [isPreVisitAnswersExpanded, setIsPreVisitAnswersExpanded] = useState(true);
  const [baselineSnapshot, setBaselineSnapshot] = useState<string | null>(null);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  const intakeFields = intakeFieldsQuery.data === undefined ? [] : intakeFieldsQuery.data;
  const outcomeOptions = outcomeOptionsQuery.data === undefined ? [] : outcomeOptionsQuery.data;
  const preVisitQuestionsByCode = buildPreVisitQuestionsByCode(
    preVisitQuestionsQuery.data === undefined ? [] : preVisitQuestionsQuery.data
  );
  const preVisitAnswers =
    consultationQuery.data === undefined || consultationQuery.data === null
      ? []
      : consultationQuery.data.preVisitAnswers;

  const savedConsultation = consultationQuery.data === undefined ? null : consultationQuery.data;

  const loadFromConsultation = useCallback(function load(consultation: FirstConsultation) {
    setResolvedConsultationId(consultation.id);
    setTherapistNotes(consultation.therapistNotes === null ? '' : consultation.therapistNotes);
    setPrivateNotes(consultation.privateNotes === null ? '' : consultation.privateNotes);
    setOutcomeCode(consultation.outcomeCode === null ? '' : consultation.outcomeCode);
    setDocuments(consultation.documents);
    const nextAnswers: Record<string, string> = {};
    for (const answer of consultation.answers) {
      nextAnswers[answer.fieldCode] = answer.value;
    }
    setAnswers(nextAnswers);
  }, []);

  useEffect(
    function initialize() {
      if (isInitialized || consultationQuery.isLoading || intakeFieldsQuery.data === undefined) {
        return;
      }
      const consultation = consultationQuery.data;
      if (consultation !== undefined && consultation !== null) {
        loadFromConsultation(consultation);
      }
      setIsInitialized(true);
    },
    [
      isInitialized,
      consultationQuery.isLoading,
      consultationQuery.data,
      intakeFieldsQuery.data,
      loadFromConsultation,
    ]
  );

  function updateAnswer(fieldCode: string, value: string) {
    setAnswers(function apply(current) {
      return { ...current, [fieldCode]: value };
    });
  }

  function buildPayload(): FirstConsultationWritePayload {
    return {
      appointmentId,
      answers: intakeFields.map(function toAnswerInput(field) {
        const value = answers[field.code];
        return { fieldCode: field.code, value: value === undefined ? '' : value };
      }),
      therapistNotes: therapistNotes.trim() === '' ? null : therapistNotes,
      privateNotes: privateNotes.trim() === '' ? null : privateNotes,
      outcomeCode: outcomeCode === '' ? null : outcomeCode,
    };
  }

  async function persist(): Promise<FirstConsultation> {
    if (resolvedConsultationId === null) {
      const created = await createMutation.mutateAsync({
        patientId: resolvedPatientId,
        payload: buildPayload(),
      });
      setResolvedConsultationId(created.id);
      setDocuments(created.documents);
      return created;
    }
    const updated = await updateMutation.mutateAsync({
      consultationId: resolvedConsultationId,
      payload: buildPayload(),
    });
    setDocuments(updated.documents);
    return updated;
  }

  async function save() {
    try {
      await persist();
      setBaselineSnapshot(null);
      showSuccessNotification(t('consultations:editor.saved'));
    } catch {
      showErrorNotification(t('consultations:editor.saveError'));
    }
  }

  async function addDocument(file: File) {
    try {
      const persisted = await persist();
      const withDocument = await uploadDocumentMutation.mutateAsync({
        consultationId: persisted.id,
        file,
      });
      setDocuments(withDocument.documents);
    } catch {
      showErrorNotification(t('consultations:editor.documentError'));
    }
  }

  async function removeDocument(documentId: string) {
    if (resolvedConsultationId === null) {
      return;
    }
    try {
      const updated = await removeDocumentMutation.mutateAsync({
        consultationId: resolvedConsultationId,
        documentId,
      });
      setDocuments(updated.documents);
    } catch {
      showErrorNotification(t('consultations:editor.documentError'));
    }
  }

  function handleDocumentSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files === null) {
      return;
    }
    const selected = files.item(0);
    if (selected !== null) {
      void addDocument(selected);
    }
    event.target.value = '';
  }

  const isBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    uploadDocumentMutation.isPending ||
    removeDocumentMutation.isPending;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const patientName = patientQuery.data === undefined ? '' : patientQuery.data.name;

  const isLoading =
    intakeFieldsQuery.isLoading ||
    outcomeOptionsQuery.isLoading ||
    consultationQuery.isLoading ||
    !isInitialized;

  const groupedFields = groupFieldsBySection(intakeFields);

  const currentSnapshot = JSON.stringify({ answers, therapistNotes, privateNotes, outcomeCode });
  const isDirty =
    isInitialized && baselineSnapshot !== null && currentSnapshot !== baselineSnapshot;

  useEffect(
    function captureBaselineSnapshot() {
      if (isInitialized && baselineSnapshot === null) {
        setBaselineSnapshot(currentSnapshot);
      }
    },
    [isInitialized, baselineSnapshot, currentSnapshot]
  );

  function navigateBackToPatient() {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
        patientId: resolvedPatientId,
      })
    );
  }

  function handleBackClick() {
    if (isDirty) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    navigateBackToPatient();
  }

  const footer = isLoading ? undefined : (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        {isDirty ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-zoowarningtext">
            <Icon name="AlertCircle" />
            {t('consultations:editor.unsavedChanges')}
          </span>
        ) : null}
      </div>
      <Button
        onClick={function onSave() {
          void save();
        }}
        disabled={isBusy}
        isLoading={isSaving}
        className="flex items-center gap-2"
      >
        {isSaving ? t('consultations:editor.saving') : t('consultations:editor.save')}
      </Button>
    </div>
  );

  return (
    <PageContainer footer={footer} width="wide">
      <Button
        variant="link"
        onClick={handleBackClick}
        className="text-zoomuted hover:text-zooprimary transition-colors mb-4"
      >
        <Icon name="ArrowLeft" />
        {t('consultations:editor.back')}
      </Button>

      {intakeFieldsQuery.isError || outcomeOptionsQuery.isError || consultationQuery.isError ? (
        <QueryErrorState
          onRetry={function retry() {
            intakeFieldsQuery.refetch();
            outcomeOptionsQuery.refetch();
            consultationQuery.refetch();
          }}
        />
      ) : isLoading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : (
        <>
          <div className="mb-5">
            <h1 className="text-2xl font-bold text-zooindigo">
              {isNew && resolvedConsultationId === null
                ? t('consultations:editor.newTitle')
                : t('consultations:editor.editTitle')}
            </h1>
            {patientName.length === 0 ? null : (
              <p className="text-sm text-zoomuted mt-0.5">{patientName}</p>
            )}
          </div>

          {preVisitAnswers.length === 0 ? null : (
            <Card className="mb-5">
              <button
                type="button"
                onClick={function toggle() {
                  setIsPreVisitAnswersExpanded(function invert(previous) {
                    return previous === false;
                  });
                }}
                className="flex items-center gap-1.5 w-full text-left"
              >
                <Icon name={isPreVisitAnswersExpanded ? 'ChevronDown' : 'ChevronRight'} />
                <p className="text-sm font-bold text-zooindigo">
                  {t('consultations:editor.preVisitAnswersTitle')}
                </p>
              </button>
              {isPreVisitAnswersExpanded ? (
                <div className="mt-4">
                  <PreVisitAnswersList
                    answers={preVisitAnswers}
                    questionsByCode={preVisitQuestionsByCode}
                    onPreviewPhoto={setPreviewedPhoto}
                  />
                </div>
              ) : null}
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            <div className="lg:col-span-2 space-y-5">
              {CONSULTATION_INTAKE_SECTION_ORDER.map(function renderSection(section) {
                const sectionFields = groupedFields[section];
                if (sectionFields === undefined || sectionFields.length === 0) {
                  return null;
                }
                return (
                  <Card key={section}>
                    <p className="text-sm font-bold text-zooindigo mb-4">
                      {t('consultations:editor.section.' + section)}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sectionFields.map(function renderField(field) {
                        return (
                          <div
                            key={field.code}
                            className={field.fieldType === 'textarea' ? 'md:col-span-2' : ''}
                          >
                            <IntakeFieldInput
                              field={field}
                              value={answers[field.code]}
                              isDisabled={isBusy}
                              onChange={function change(value) {
                                updateAnswer(field.code, value);
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}

              <Card>
                <p className="text-sm font-bold text-zooindigo mb-2">
                  {t('consultations:editor.outcomeLabel')}
                </p>
                <SearchableSelect
                  inputId="first-consultation-outcome"
                  ariaLabel={t('consultations:editor.outcomeLabel')}
                  placeholder={t('consultations:editor.outcomePlaceholder')}
                  value={outcomeCode}
                  isDisabled={isBusy}
                  isClearable
                  options={outcomeOptions.map(function toOption(option) {
                    return {
                      value: option.code,
                      label: localizedLabel(option.labelEn, option.labelPl),
                    };
                  })}
                  onChange={function pickOutcome(nextValue) {
                    setOutcomeCode(nextValue);
                  }}
                />
              </Card>

              <FirstConsultationNotesCards
                sharedNotes={therapistNotes}
                privateNotes={privateNotes}
                isDisabled={isBusy}
                onSharedNotesChange={setTherapistNotes}
                onPrivateNotesChange={setPrivateNotes}
              />
            </div>

            <div className="space-y-5">
              {savedConsultation === null ? null : (
                <FirstConsultationSharingCard
                  consultation={savedConsultation}
                  outcomeOptions={outcomeOptions}
                  hasUnsavedChanges={isDirty}
                  isEditorBusy={isBusy}
                />
              )}
              <Card>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-zooindigo">
                    {t('consultations:editor.documents')}
                  </p>
                  <Button
                    variant="link"
                    disabled={isBusy}
                    onClick={function selectDocument() {
                      if (documentInputRef.current !== null) {
                        documentInputRef.current.click();
                      }
                    }}
                    className="text-xs"
                  >
                    <Icon name="Upload" />
                    {t('consultations:editor.uploadDocument')}
                  </Button>
                </div>
                {documents.length === 0 ? (
                  <p className="text-xs text-zoomuted mt-3">
                    {t('consultations:editor.noDocuments')}
                  </p>
                ) : (
                  <ul className="space-y-2 mt-3">
                    {documents.map(function renderDocument(consultationDocument) {
                      const isRemovingThisDocument =
                        removeDocumentMutation.isPending &&
                        removeDocumentMutation.variables !== undefined &&
                        removeDocumentMutation.variables.documentId === consultationDocument.id;
                      return (
                        <li
                          key={consultationDocument.id}
                          className="flex items-center justify-between gap-2 rounded-xl border border-zooborder px-3 py-2"
                        >
                          <button
                            type="button"
                            onClick={function preview() {
                              setPreviewedDocument(consultationDocument);
                            }}
                            className="flex items-center gap-2 min-w-0 text-sm font-semibold text-zooprimary hover:underline text-left"
                          >
                            <Icon name="FileText" />
                            <span className="truncate">{consultationDocument.filename}</span>
                          </button>
                          <Button
                            variant="linkDanger"
                            disabled={isBusy}
                            isLoading={isRemovingThisDocument}
                            aria-label={t('consultations:editor.removeDocument')}
                            onClick={function remove() {
                              void removeDocument(consultationDocument.id);
                            }}
                            className="shrink-0"
                          >
                            {isRemovingThisDocument ? null : <Icon name="Trash" />}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <input
                  ref={documentInputRef}
                  type="file"
                  accept={FIRST_CONSULTATION_DOCUMENT_ACCEPT_ATTRIBUTE}
                  className="hidden"
                  onChange={handleDocumentSelected}
                />
              </Card>
            </div>
          </div>
        </>
      )}

      {isLeaveConfirmOpen === true ? (
        <ConfirmDialog
          title={t('consultations:editor.leaveTitle')}
          description={t('consultations:editor.leaveMessage')}
          confirmLabel={t('consultations:editor.leaveConfirm')}
          tone="danger"
          iconName="AlertCircle"
          onConfirm={navigateBackToPatient}
          onCancel={function cancelLeave() {
            setIsLeaveConfirmOpen(false);
          }}
        />
      ) : null}

      <FilePreviewModal
        isOpen={previewedDocument !== null}
        onClose={function closePreview() {
          setPreviewedDocument(null);
        }}
        url={previewedDocument === null ? '' : previewedDocument.url}
        filename={previewedDocument === null ? '' : previewedDocument.filename}
        contentType={previewedDocument === null ? null : previewedDocument.contentType}
      />

      <FilePreviewModal
        isOpen={previewedPhoto !== null}
        onClose={function closePreview() {
          setPreviewedPhoto(null);
        }}
        url={previewedPhoto === null ? '' : previewedPhoto.url}
        filename={previewedPhoto === null ? '' : previewedPhoto.filename}
        contentType={previewedPhoto === null ? null : previewedPhoto.contentType}
      />
    </PageContainer>
  );
}

interface IntakeFieldInputProps {
  field: IntakeField;
  value: string | undefined;
  isDisabled: boolean;
  onChange: (value: string) => void;
}

function IntakeFieldInput({ field, value, isDisabled, onChange }: IntakeFieldInputProps) {
  const label = localizedLabel(field.labelEn, field.labelPl);
  const resolvedValue = value === undefined ? '' : value;

  if (field.fieldType === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm text-zooindigo">
        <input
          type="checkbox"
          checked={resolvedValue === 'true'}
          disabled={isDisabled}
          onChange={function change(event) {
            onChange(event.target.checked ? 'true' : 'false');
          }}
          className="w-4 h-4 accent-zooprimary"
        />
        {label}
      </label>
    );
  }

  if (field.fieldType === 'textarea') {
    return (
      <label className="block text-xs font-semibold text-zoomuted">
        {label}
        <AutoGrowTextarea
          value={resolvedValue}
          disabled={isDisabled}
          rows={3}
          onChange={function change(event) {
            onChange(event.target.value);
          }}
          className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
        />
      </label>
    );
  }

  return (
    <label className="block text-xs font-semibold text-zoomuted">
      {label}
      <AutoGrowTextarea
        value={resolvedValue}
        disabled={isDisabled}
        onChange={function change(event) {
          onChange(event.target.value);
        }}
        className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
      />
    </label>
  );
}
